import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useCart } from '@/context/CartContext';
import { Trash2, Plus, Minus, ArrowLeft, ShoppingBag } from 'lucide-react';
import axios from 'axios';
import { resolveImageUrl } from '../utils/imageUrl';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CartPage = () => {
  const { cart, removeFromCart, updateQuantity, getCartSubtotal, calculatePrice, clearCart } = useCart();
  const [showQuoteForm, setShowQuoteForm] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    message: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const navigate = useNavigate();

  const handleQuantityChange = (productId, newQuantity) => {
    if (newQuantity < 1) return;
    updateQuantity(productId, newQuantity);
  };

  const handleSubmitQuote = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const quoteData = {
        ...formData,
        cart_items: cart.map(item => ({
          product_id: item.product.id,
          product_name: item.product.full_name,
          quantity: item.quantity,
          unit_price: calculatePrice(item.product.price_tiers, item.quantity),
          total: calculatePrice(item.product.price_tiers, item.quantity) * item.quantity
        })),
        total: getCartSubtotal()
      };

      await axios.post(`${API}/quotes`, quoteData);
      setSubmitted(true);
      clearCart();
      
      // Redirect after 3 seconds
      setTimeout(() => {
        navigate('/');
      }, 3000);
    } catch (error) {
      console.error('Error submitting quote:', error);
      alert('Failed to submit quote. Please try again or contact us directly.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="container mx-auto px-4 py-12">
        <div className="max-w-2xl mx-auto text-center">
          <div className="bg-green-50 border border-green-200 rounded-lg p-8">
            <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="text-3xl font-bold text-green-900 mb-4">Quote Request Submitted!</h2>
            <p className="text-gray-700 mb-6">
              Thank you for your quote request. We'll get back to you shortly at {formData.email}
            </p>
            <p className="text-sm text-gray-600">Redirecting to homepage...</p>
          </div>
        </div>
      </div>
    );
  }

  if (cart.length === 0) {
    return (
      <div className="container mx-auto px-4 py-12">
        <div className="max-w-2xl mx-auto text-center">
          <ShoppingBag size={64} className="mx-auto text-gray-400 mb-4" />
          <h2 className="text-3xl font-bold mb-4">Your cart is empty</h2>
          <p className="text-gray-600 mb-8">Add some products to get started!</p>
          <Link
            to="/products"
            className="inline-flex items-center gap-2 bg-blue-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-blue-700 transition"
          >
            Browse Products
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-12">
      <Link
        to="/products"
        className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 mb-8"
      >
        <ArrowLeft size={20} />
        Continue Shopping
      </Link>

      <h1 className="text-4xl font-bold mb-8" data-testid="cart-page-title">Shopping Cart</h1>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Cart Items */}
        <div className="lg:col-span-2">
          <div className="bg-white rounded-lg shadow-md divide-y">
            {cart.map((item) => {
              const unitPrice = calculatePrice(item.product.price_tiers, item.quantity);
              const totalPrice = unitPrice * item.quantity;

              return (
                <div key={item.product.id} className="p-4 md:p-6" data-testid={`cart-item-${item.product.id}`}>
                  <div className="flex flex-col sm:flex-row gap-4 sm:gap-6">
                    <div className="relative bg-gray-100 rounded-lg w-20 h-20 sm:w-24 sm:h-24 flex-shrink-0 overflow-hidden mx-auto sm:mx-0">
                      <img
                        src={resolveImageUrl(item.product.image_url)}
                        alt={item.product.full_name}
                        className="w-full h-full object-cover"
                      />
                    </div>

                    <div className="flex-grow">
                      <h3 className="text-lg sm:text-xl font-semibold mb-1">{item.product.full_name}</h3>
                      <p className="text-gray-600 text-sm mb-3">{item.product.weight}</p>

                      <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 mb-3">
                        <div className="flex items-center border border-gray-300 rounded-lg w-fit">
                          <button
                            onClick={() => handleQuantityChange(item.product.id, item.quantity - 1)}
                            className="px-2 sm:px-3 py-1 hover:bg-gray-100 touch-manipulation"
                            data-testid={`decrease-quantity-${item.product.id}`}
                          >
                            <Minus size={16} />
                          </button>
                          <span className="px-3 sm:px-4 py-1 border-x min-w-[40px] text-center">{item.quantity}</span>
                          <button
                            onClick={() => handleQuantityChange(item.product.id, item.quantity + 1)}
                            className="px-2 sm:px-3 py-1 hover:bg-gray-100 touch-manipulation"
                            data-testid={`increase-quantity-${item.product.id}`}
                          >
                            <Plus size={16} />
                          </button>
                        </div>

                        <button
                          onClick={() => removeFromCart(item.product.id)}
                          className="text-red-600 hover:text-red-700 flex items-center gap-1 text-sm md:text-base"
                          data-testid={`remove-item-${item.product.id}`}
                        >
                          <Trash2 size={16} className="flex-shrink-0" />
                          <span className="whitespace-nowrap">Remove</span>
                        </button>
                      </div>

                      <div className="text-sm sm:text-base text-gray-600 mt-2">
                        <p className="flex items-center gap-1 flex-wrap">
                          <span>${unitPrice.toFixed(2)} × {item.quantity} =</span>
                          <span className="font-semibold text-lg sm:text-xl text-gray-900">${totalPrice.toFixed(2)}</span>
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Order Summary */}
        <div>
          <div className="bg-white rounded-lg shadow-md p-6 sticky top-24">
            <h2 className="text-2xl font-bold mb-4">Order Summary</h2>
            <div className="border-t border-b py-4 mb-4">
              <div className="flex justify-between mb-2">
                <span className="text-gray-600">Subtotal</span>
                <span className="font-semibold">${getCartSubtotal().toFixed(2)}</span>
              </div>
              <div className="flex justify-between mb-2">
                <span className="text-gray-600">Shipping</span>
                <span className="font-semibold text-green-600">FREE</span>
              </div>
            </div>
            <div className="flex justify-between mb-6">
              <span className="text-xl font-bold">Total</span>
              <span className="text-2xl font-bold text-blue-600" data-testid="cart-total">${getCartSubtotal().toFixed(2)}</span>
            </div>
            <button
              onClick={() => setShowQuoteForm(true)}
              className="w-full bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700 transition"
              data-testid="request-quote-button"
            >
              Request Quote
            </button>
            <p className="text-xs text-gray-500 mt-3 text-center">
              We'll contact you with shipping details and payment options
            </p>
          </div>
        </div>
      </div>

      {/* Quote Form Modal */}
      {showQuoteForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto p-8">
            <h2 className="text-3xl font-bold mb-6">Request a Quote</h2>
            <form onSubmit={handleSubmitQuote}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-semibold mb-2">Name *</label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-4 py-2"
                    data-testid="quote-name-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold mb-2">Email *</label>
                  <input
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-4 py-2"
                    data-testid="quote-email-input"
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-semibold mb-2">Phone *</label>
                  <input
                    type="tel"
                    required
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-4 py-2"
                    data-testid="quote-phone-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold mb-2">Company</label>
                  <input
                    type="text"
                    value={formData.company}
                    onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-4 py-2"
                  />
                </div>
              </div>
              <div className="mb-6">
                <label className="block text-sm font-semibold mb-2">Additional Message</label>
                <textarea
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  rows={4}
                  className="w-full border border-gray-300 rounded-lg px-4 py-2"
                  placeholder="Any special requirements or questions?"
                ></textarea>
              </div>
              <div className="flex gap-4">
                <button
                  type="button"
                  onClick={() => setShowQuoteForm(false)}
                  className="flex-1 bg-gray-200 text-gray-700 px-6 py-3 rounded-lg font-semibold hover:bg-gray-300 transition"
                  disabled={submitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700 transition disabled:bg-blue-400"
                  disabled={submitting}
                  data-testid="submit-quote-button"
                >
                  {submitting ? 'Submitting...' : 'Submit Quote Request'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default CartPage;